/* eslint-disable react-hooks/exhaustive-deps */
import {
  DeleteOutlined,
  LoadingOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Avatar,
  Button,
  Divider,
  Dropdown,
  message,
  Modal,
  Radio,
  Tag,
  Timeline,
  Typography,
} from 'antd';
import Header from 'components/header';
import PageWrapper from 'components/pageWrapper';
import React, { useContext, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { deleteRecognition, getUserFeed, getUserProfile } from 'utils/api';
import styles from 'styles/pages/user-details.module.scss';
import ClapIcon from 'components/clapIcon';
import { transformCompanyValues, useWindowSize } from 'utils/utils';
import { ITEM_TYPES } from 'utils/constants';
import Pie from 'components/charts/pie';
import { shortnamesToUnicode } from 'emojitsu';
import pluralize from 'pluralize';
import ProfileContext from 'components/profileContext';
const { Text } = Typography;

const Item = ({ data, onDelete, deleteLoading }) => {
  let renderType = null;

  switch (data.itemType) {
    case ITEM_TYPES.GIVEN:
      renderType = (
        <>
          <div>
            {`Gave `}
            <span className={styles.number}>{`${data.points} `}</span>
            <ClapIcon />
            {data.shoutOutId ? (
              <span
                style={{
                  textDecoration: 'underline',
                  marginLeft: 2,
                  marginRight: 2,
                }}
              >
                additional {pluralize('clap', data.points)}
              </span>
            ) : (
              ''
            )}
            {` to `}
            {data.recipients.map((rec, i) => (
              <span key={rec._id}>
                <a href={`/#/users/${rec._id}`} key={rec._id}>
                  {rec.name}
                </a>
                {i < data.recipients.length - 1 ? ', ' : ''}
              </span>
            ))}
          </div>

          {data.shoutOutId ? (
            <></>
          ) : (
            <div className={styles.message}>
              <Text type="secondary" italic>
                {`"${data.message}"`}
              </Text>
            </div>
          )}
          {!data.shoutOutId &&
            data.companyValues &&
            data.companyValues.length > 0 && (
              <div className={styles.values}>
                {data.companyValues.map((c) => (
                  <Tag bordered={false} color="green" key={c}>
                    {shortnamesToUnicode(c)}
                  </Tag>
                ))}
              </div>
            )}
        </>
      );
      break;
    case ITEM_TYPES.RECEIVED:
      renderType = (
        <>
          <div>
            {`Received `}
            <span className={styles.number}>{`${data.points} `}</span>
            <ClapIcon />
            {` from `}
            {data.giver._id === 'INIT' || data.giver._id === 'ADMIN' ? (
              { INIT: 'initialization', ADMIN: 'an admin' }[data.giver._id]
            ) : (
              <a href={`/#/users/${data.giver._id}`}>{data.giver.name}</a>
            )}
          </div>
          <div className={styles.message}>
            <Text type="secondary" italic>
              {`"${data.message}"`}
            </Text>
          </div>
          {data.companyValues && data.companyValues.length > 0 && (
            <div className={styles.values}>
              {data.companyValues.map((c) => (
                <Tag bordered={false} color="green" key={c}>
                  {shortnamesToUnicode(c)}
                </Tag>
              ))}
            </div>
          )}
          {data.additionalClaps && data.additionalClaps.length > 0 && (
            <div style={{ marginTop: 4 }}>
              Also received{' '}
              <span style={{ textDecoration: 'underline' }}>
                additional claps
              </span>
              :{' '}
              {data.additionalClaps.map((additional) => (
                <div
                  key={additional._id}
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <span
                    className={styles.number}
                  >{`${additional.points} `}</span>
                  <ClapIcon />
                  &nbsp;
                  <a href={`/#/users/${additional.giver._id}`}>
                    {additional.giver.name}
                  </a>
                </div>
              ))}
            </div>
          )}
        </>
      );
      break;
    case ITEM_TYPES.CLAIM:
      renderType = (
        <div>
          {`Claimed a reward:`}
          <Text mark> {data.reward.title}</Text>
          {` for `}
          <span className={styles.number}>{data.reward.amount}</span>
          <ClapIcon />
        </div>
      );
      break;
    default:
      renderType = <></>;
  }

  const items = [
    {
      disabled: deleteLoading,
      key: '1',
      icon: <DeleteOutlined />,
      label: 'Delete',
      onClick: () => {
        Modal.confirm({
          type: 'danger',
          title: 'Delete recognition',
          content: (
            <div>
              Are you sure you want to delete this recognition?
              <Alert
                style={{ marginTop: 10 }}
                showIcon
                message="it will also delete all the additional claps given by others with 'clap this too'"
                type="warning"
              />
              <div style={{ marginTop: 10 }}>This cannot be undone.</div>
            </div>
          ),
          okText: 'Yes, Delete',
          cancelText: 'No, keep it',
          onOk: () => {
            if (!deleteLoading) {
              onDelete(data._id);
            }
          },
        });
      },
    },
  ];

  return (
    <div key={data._id} className={styles.recognitionWrapper}>
      <div className={styles.recognitionContent}>{renderType}</div>
      <div className={styles.actions}>
        <Dropdown menu={{ items }} trigger={['click']}>
          <Button
            type="link"
            size="small"
            style={{ backgroundColor: '#eeeeee' }}
          >
            <MoreOutlined />
          </Button>
        </Dropdown>
      </div>
    </div>
  );
};

const UserDetails = () => {
  const [userProfile, setUserProfile] = useState(null);
  const [companyValues, setCompanyValues] = useState(null);
  const [hideShowMore, setHideShowMore] = useState(false);
  const [itemType, setItemType] = useState(ITEM_TYPES.ALL);
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile } = useContext(ProfileContext);
  const size = useWindowSize();
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [feed, setFeed] = useState(null);
  const { id } = useParams();

  const getUserFeedData = (itemType = 'ALL', after = null) =>
    getUserFeed({
      userId: id,
      feedType: itemType,
      ...(after ? { after: after } : {}),
    }).then((data) => {
      setFeed(
        after ? { ...feed, items: [...feed.items, ...data.items] } : data
      );
      if (data.items && data.items.length < 20) {
        setHideShowMore(true);
      }
    });

  const onDelete = (id) => {
    setDeleteLoading(true);
    deleteRecognition({ shoutOutId: id, cascade: true })
      .then((data) => {
        getUserFeedData();
      })
      .catch((e) => {
        console.error(e);
        message.error('Failed to delete recognition');
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  };

  useEffect(() => {
    //get user profile
    getUserProfile({ userId: id }).then((data) => {
      setUserProfile(data);
      setCompanyValues(
        transformCompanyValues(data.stats.companyValues, profile.companyValues)
      );
    });
    const currentType = searchParams.get('type') || 'ALL';
    setItemType(currentType);
    getUserFeedData(currentType);
  }, [id]);

  const onChangeType = ({ target: { value } }) => {
    setItemType(value);
    getUserFeedData(value);
    searchParams.set('type', value);
    setSearchParams(searchParams);
  };

  const showMore = () => {
    getUserFeedData(itemType, feed.items[feed.items.length - 1]._id);
  };

  return (
    <>
      <Header />
      <PageWrapper>
        <div className={styles.wrapper}>
          <div className={styles.leftWrapper}>
            <div className={styles.leftInner}>
              <Avatar size={96} src={userProfile?.profilePicture} />
              <h1 className={styles.name}>{userProfile?.name}</h1>
              <div className={styles.stats}>
                <div className={styles.statElem}>
                  {`Received:`}{' '}
                  <span className={styles.number}>{`${userProfile?.stats.receivedPoints || '0'
                    } `}</span>{' '}
                  <ClapIcon />
                </div>
                <Divider type="vertical" />
                <div className={styles.statElem}>
                  {`Given:`}{' '}
                  <span className={styles.number}>{`${userProfile?.stats.givenPoints || '0'
                    } `}</span>{' '}
                  <ClapIcon />
                </div>
                <Divider type="vertical" />
                <div className={styles.statElem}>
                  {`Redeemable:`}{' '}
                  <span className={styles.number}>{`${userProfile?.stats.redeemablePoints || '0'
                    } `}</span>{' '}
                  <ClapIcon />
                </div>
              </div>
              <div>
                {companyValues && companyValues.length > 0 && (
                  <Pie
                    simpleLabels
                    data={companyValues}
                    width={size.width < 960 ? size.width - 72 : 380}
                  />
                )}
              </div>
            </div>
          </div>
          <div className={styles.rightWrapper}>
            <div className={styles.rightTitle}>
              <h2 className={styles.feedTitle}>Feed</h2>
              <div>
                <Radio.Group
                  size="small"
                  options={Object.keys(ITEM_TYPES).map((e) => ({
                    key: e,
                    label: `${e.substring(0, 1)}${e
                      .substring(1, e.length)
                      .toLowerCase()}`,
                    value: e,
                  }))}
                  onChange={onChangeType}
                  value={itemType}
                  optionType="button"
                  buttonStyle="solid"
                />
              </div>
            </div>

            {!userProfile && <LoadingOutlined />}
            <Timeline
              className={styles.timeline}
              mode={'left'}
              items={feed?.items?.map((item, i) => {
                return {
                  id: item?._id,
                  key: `${item?._id}_${i}`,
                  color:
                    item.itemType === ITEM_TYPES.GIVEN ? '#0d5181' : 'green',
                  label: (
                    <span className={styles.date}>
                      {new Date(item.date).toLocaleString('en-US')}
                    </span>
                  ),
                  children: <Item data={item} key={item._id} onDelete={onDelete} deleteLoading={deleteLoading} />,
                };
              })}
            />
            {!hideShowMore && (
              <div className={styles.showmore}>
                <Button type="link" onClick={showMore}>
                  Show more
                </Button>
              </div>
            )}
          </div>
        </div>
      </PageWrapper>
    </>
  );
};

export default UserDetails;
